import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Briefcase, Users, Check } from 'lucide-react';

const models = [
  {
    title: "Fixed Price",
    icon: Briefcase,
    description: "Ideal for projects with well-defined requirements and timelines.",
    features: [
      "Clear budget & timeline",
      "Defined deliverables",
      "No hidden costs",
      "Milestone-based payment"
    ],
    color: "blue"
  },
  {
    title: "Hourly / T&M",
    icon: Clock,
    description: "Flexible model for evolving projects where requirements change.",
    features: [
      "Pay as you go",
      "Flexible scope",
      "Regular updates",
      "Scalable resources"
    ],
    color: "teal"
  },
  {
    title: "Dedicated Team",
    icon: Users,
    description: "Your own extended team working exclusively on your projects.",
    features: [
      "Full control over team",
      "Direct communication",
      "Long-term collaboration",
      "Cost-effective scaling"
    ],
    color: "indigo"
  }
];

const EngagementModels = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <section className="py-20 lg:py-24 bg-white">
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-1 rounded-full bg-teal-100 text-teal-900 font-medium text-sm mb-4">
            Engagement Models
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Flexible Ways to Work With Us
          </h2>
          <p className="text-lg text-gray-600">
             Choose the model that best fits your business needs and project goals.
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {models.map((model, idx) => {
            const Icon = model.icon;
            return (
              <motion.div
                key={idx}
                variants={itemVariants}
                className={`bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-${model.color}-500 transition-colors duration-300 relative overflow-hidden group`}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-${model.color}-50 rounded-bl-full -mr-8 -mt-8 opacity-50 group-hover:scale-110 transition-transform duration-500`}></div>
                
                <div className={`w-14 h-14 bg-${model.color}-100 rounded-xl flex items-center justify-center mb-6 text-${model.color}-600 relative z-10`}>
                  <Icon size={28} />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 relative z-10">
                  {model.title}
                </h3>
                <p className="text-gray-600 mb-8 relative z-10 min-h-[48px]">
                  {model.description}
                </p>
                
                <ul className="space-y-3 relative z-10">
                  {model.features.map((feature, i) => (
                    <li key={i} className="flex items-start text-sm text-gray-600">
                      <Check size={16} className={`text-${model.color}-500 mr-2 mt-0.5`} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default EngagementModels;
